import React, { memo, useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { dates, validators } from "investira.sdk";
import { useParams } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  Stack,
  LoadingButton,
  Skeleton,
  Icon,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  DialogActions,
  Card,
  FormLabel,
  Chip,
} from "investira.react.components";
import { CARTEIRA_TIPO } from "../../enums";
import { VALIDATIONS } from "../../const";
import { useCarteirasContext } from "../../contexts/CarteirasContext";
import services from "../../services";
import withResponseHandling from "../../hoc/withResponseHandling";
import CalendarPosicoes from "./CalendarPosicoes";

const TODAY = new Date();

const DialogCloneCarteira = memo((props) => {
  const { state, actions } = useCarteirasContext();
  const [carteiras, setCarteiras] = useState();
  const [tipo, setTipo] = useState("T");
  const [datas, setDatas] = useState([]);
  const params = useParams();

  const isEdit = !validators.isNull(state.posicaoSelected.id);
  const isOpen = state.isDialogOpen && state.dialogType === "clone";

  const xInitialValues = {
    origem_carteira_id: "",
    origem_data: TODAY,
    destino_data: TODAY,
    sobreescrever: 1,
  };

  const formik = useFormik({
    initialValues: xInitialValues,

    validationSchema: Yup.object().shape({
      origem_carteira_id: Yup.string().required(VALIDATIONS.REQUIRED),
      origem_data: Yup.date()
        .required(VALIDATIONS.REQUIRED)
        .typeError(`${VALIDATIONS.INVALID_DATE}`),
      destino_data: Yup.date()
        .required(VALIDATIONS.REQUIRED)
        .typeError(`${VALIDATIONS.INVALID_DATE}`),
    }),
    onSubmit: (pValues, pActions) => {
      const xData = {
        origem_carteira_id: pValues.origem_carteira_id,
        origem_data: pValues.origem_data,
        destino_carteira_id: state.carteira_id,
        destino_data:
          dates.toUTC(dates.startOf("day", pValues.destino_data)) ||
          state.data_posicao ||
          dates.toUTC(dates.startOf("day", new Date())),
        sobrescrever: Boolean(Number(pValues.sobreescrever)),
      };

      actions.clonePosicao(
        xData,
        () => {
          actions.handleClonePosicaoCancel();
        },
        (rErr) => {
          props.responseErrorHandling(rErr);
        },
        () => {
          pActions.setSubmitting(false);
        }
      );
    },
  });

  const xCarteiraDefaultValue = useMemo(
    () => ({ carteira: formik.values.carteira }),
    [formik.values.carteira]
  );

  const handleTipoChange = (event) => {
    setTipo(event.target.value);
  };

  const handleCarteiraClick = (pFieldName, pFieldValue) => {
    formik.setFieldValue(pFieldName, pFieldValue, true);
  };

  const handleClose = () => {
    actions.handleClonePosicaoCancel();
  };

  function filterCarteira(pCarteiras, pSelectedCarteiraId, pRemoveCarteiraId) {
    let xCarteiras = pCarteiras;

    if (pRemoveCarteiraId) {
      xCarteiras = xCarteiras.filter((xCarteira) => {
        return xCarteira.carteira_id !== pRemoveCarteiraId;
      });
    }

    if (pSelectedCarteiraId) {
      return xCarteiras.filter((xCarteira) => {
        return xCarteira.carteira_id === pSelectedCarteiraId;
      });
    }

    return xCarteiras;
  }

  useEffect(() => {
    // EDIT
    if (isEdit) {
      formik.setValues(state.posicaoSelected);
    } else {
      formik.setValues(xInitialValues);
    }
  }, [isEdit, state.posicaoSelected]);

  useEffect(() => {
    if (!validators.isEmpty(formik.values.origem_carteira_id)) {
      services.posicoes.datas(
        {
          sort: "data DESC",
          carteira_id: formik.values.origem_carteira_id,
        },
        (rRes) => {
          const xDates = rRes.data;
          // const xDates = rRes.data.map((xPosicao) => {
          //   return {
          //     value: xPosicao.data,
          //     label: formats.formatDateCustom(
          //       dates.toDate(xPosicao.data),
          //       "DD/MM/YYYY"
          //     ),
          //   };
          // });
          console.log("REQ => ", xDates);
          setDatas(xDates);
          formik.setFieldValue("origem_data", xDates[0].data);
        },
        (rErr) => {
          props.responseErrorHandling(rErr);
        }
      );
    }
  }, [isOpen, formik.values.origem_carteira_id]);

  useEffect(() => {
    if (!isOpen) {
      formik.resetForm({ values: xInitialValues });
      setDatas([]);
    }

    if (isOpen) {
      setDatas([]);
      handleCarteiraClick("origem_carteira_id", "");

      services.carteiras.list(
        { carteira_tipo_id: tipo },
        (rRes) => {
          setCarteiras(Object.values(rRes.data));
        },
        (rErr) => {
          props.responseErrorHandling(rErr);
        }
      );
    }
  }, [isOpen, tipo]);

  return (
    <Dialog open={isOpen} maxWidth="lg" fullWidth onClose={handleClose}>
      <DialogTitle>Copiar Posição</DialogTitle>
      <DialogContent>
        <Stack
          sx={{ marginBottom: 2, py: 1 }}
          spacing={4}
          direction="row"
          alignItems="center"
        >
          <Stack
            spacing={2}
            flex="1"
            sx={{ minHeight: "660px", maxHeight: "660px" }}
          >
            <Card variant="outlined" sx={{ flexGrow: 1 }}>
              <Stack spacing={2} flex="1" p={2}>
                <Typography variant="body2" color="textSecondary">
                  Origem:
                </Typography>
                <Divider />
                {validators.isEmpty(formik.values.origem_carteira_id) && (
                  <Stack>
                    <FormControl>
                      <FormLabel id="tipo-label">
                        <Typography variant={"caption"}>Tipo</Typography>
                      </FormLabel>
                      <RadioGroup
                        row
                        name="carteira_tipo_id"
                        value={tipo}
                        onChange={handleTipoChange}
                      >
                        {Object.values(CARTEIRA_TIPO)
                          .filter((xTipo) => xTipo.id !== "C")
                          .map((xTipo) => {
                            return (
                              <FormControlLabel
                                key={xTipo.id}
                                value={xTipo.id}
                                control={<Radio size="small" />}
                                label={xTipo.label}
                                componentsProps={{
                                  typography: {
                                    variant: "caption",
                                  },
                                }}
                              />
                            );
                          })}
                      </RadioGroup>
                    </FormControl>
                    <Divider sx={{ mt: 1 }} />
                  </Stack>
                )}
                <Stack
                  mb={2}
                  sx={{ flexGrow: 1, overflowY: "scroll", height: "495px" }}
                >
                  <form noValidate>
                    <Stack flex="1">
                      <List sx={{ marginBottom: 2 }}>
                        {carteiras &&
                          filterCarteira(
                            carteiras,
                            formik.values.origem_carteira_id,
                            params.id
                          ).map((xCarteira, xIndex) => {
                            const isDisabled = !Boolean(xCarteira.data_posicao);
                            return (
                              <React.Fragment key={xCarteira.carteira_id}>
                                {xIndex > 0 && <Divider component="li" />}
                                <ListItemButton
                                  disabled={isDisabled}
                                  onClick={() =>
                                    !isDisabled
                                      ? handleCarteiraClick(
                                          "origem_carteira_id",
                                          xCarteira.carteira_id
                                        )
                                      : null
                                  }
                                  secondaryAction={
                                    formik.values.carteira_id && (
                                      <IconButton
                                        color="primary"
                                        onClick={(pEvent) => {
                                          pEvent.preventDefault();
                                          pEvent.stopPropagation();
                                          handleCarteiraClick(
                                            "origem_carteira_id",
                                            ""
                                          );
                                        }}
                                      >
                                        <Icon iconName="cancel" size={16} />
                                      </IconButton>
                                    )
                                  }
                                  sx={{ cursor: "pointer" }}
                                >
                                  <ListItemText
                                    primary={xCarteira.carteira_seuid}
                                    secondary={
                                      <Stack
                                        direction="row"
                                        spacing={2}
                                        alignItems="center"
                                      >
                                        <Typography variant="caption">
                                          {xCarteira.carteira}
                                        </Typography>
                                        {isDisabled && (
                                          <Chip
                                            label="Sem posição"
                                            size="small"
                                            color="secondary"
                                          />
                                        )}
                                      </Stack>
                                    }
                                  />
                                </ListItemButton>
                              </React.Fragment>
                            );
                          })}
                      </List>
                    </Stack>
                    <Stack flex="1">
                      {!validators.isEmpty(
                        formik.values.origem_carteira_id
                      ) && (
                        <Stack flex={1} spacing={2}>
                          {validators.isEmpty(dates) ? (
                            <Skeleton
                              variant="rounded"
                              animation="wave"
                              height={32}
                            />
                          ) : (
                            <>
                              <Divider />
                              <Stack flexGrow={1} px={2}>
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                >
                                  Data posição
                                </Typography>
                                <Stack
                                  flexGrow={1}
                                  px={2}
                                  justifyContent="center"
                                >
                                  <CalendarPosicoes
                                    key={"data-posicao-origem"}
                                    id="origem_data"
                                    name="origem_data"
                                    carteiraId={
                                      formik.values.origem_carteira_id
                                    }
                                    onStart={(pData) =>
                                      formik.setFieldValue("origem_data", pData)
                                    }
                                    onChange={({ data }) => {
                                      formik.setFieldValue(
                                        "origem_data",
                                        data.format()
                                      );
                                    }}
                                    dataPosicao={
                                      !validators.isEmpty(datas)
                                        ? new Date(datas[0].data)
                                        : null
                                    }
                                    datasPosicoes={datas}
                                    onlyHighlightAble={true}
                                    refreshHighlightedDays={true}
                                  />
                                </Stack>
                              </Stack>
                              <Divider />
                            </>
                          )}
                        </Stack>
                      )}
                    </Stack>
                  </form>
                </Stack>
              </Stack>
            </Card>
          </Stack>
          <Stack spacing={2} flex="0">
            <Icon iconName="arrow_right" color="primary" />
          </Stack>
          <Stack
            spacing={2}
            flex="1"
            sx={{ minHeight: "660px", maxHeight: "660px" }}
          >
            <Card variant="outlined" sx={{ flexGrow: 1 }}>
              <Stack spacing={2} flex="1" p={2}>
                <Typography variant="body2" color="textSecondary">
                  Destino:
                </Typography>
                <Divider />
                <List>
                  <ListItem>
                    <ListItemText
                      primary={state.carteira_seuid}
                      secondary={
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Typography variant="caption">
                            {state.carteira}
                          </Typography>
                        </Stack>
                      }
                    />
                  </ListItem>
                </List>
                {/* <Divider />
                <Stack direction="row" spacing={2} px={2}>
                  <Stack>
                    <Typography variant="caption" color="textSecondary">
                      Tipo
                    </Typography>
                    <Typography variant="body1" color="textPrimary">
                      {CARTEIRA_TIPO[state.carteira_tipo_id].label}
                    </Typography>
                  </Stack>
                </Stack> */}
                <Divider />
                <Stack direction="row" spacing={2} px={2}>
                  <Stack flexGrow={1}>
                    <Typography variant="caption" color="textSecondary">
                      Data posição
                    </Typography>
                    <Stack flexGrow={1} px={2} justifyContent="center">
                      <CalendarPosicoes
                        key={"data-posicao-destino"}
                        id="destino_data"
                        name="destino_data"
                        carteiraId={state.carteira_id}
                        dataPosicao={new Date()}
                        onChange={({ data }) => {
                          formik.setFieldValue("destino_data", data.format());
                        }}
                      />
                    </Stack>
                  </Stack>
                </Stack>

                <Divider />
                <Stack flex="1" px={2}>
                  <FormControl>
                    <FormLabel>Posição:</FormLabel>
                    <RadioGroup
                      row
                      name="sobreescrever"
                      value={formik.values.sobreescrever}
                      onChange={formik.handleChange}
                    >
                      <FormControlLabel
                        value={1}
                        control={<Radio />}
                        label="Sobreescrever"
                      />
                      <FormControlLabel
                        value={0}
                        control={<Radio />}
                        label="Adicionar"
                      />
                    </RadioGroup>
                  </FormControl>
                </Stack>
              </Stack>
            </Card>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          type="button"
          color="primary"
          onClick={handleClose}
        >
          Cancelar
        </Button>
        <LoadingButton
          variant="contained"
          type="button"
          onClick={formik.handleSubmit}
          loading={formik.isSubmitting}
          disabled={
            validators.isEmpty(formik.values.origem_carteira_id) ||
            formik.isSubmitting ||
            formik.initialValid
          }
        >
          Copiar
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
});

export default withResponseHandling(DialogCloneCarteira);
